/**
 * Marker Icon Types
 *
 * Centralized icon key definitions ensuring consistency across:
 * - MapToolbox icon selector
 * - TaskMarker renderer
 * - PreviewMarker renderer
 * - Icon mapping utilities
 *
 * @module types/MarkerIcon
 */

/**
 * Marker Icon Keys (Enum-like)
 *
 * Uses const object for zero runtime overhead + full type safety.
 * All icon keys use kebab-case convention for consistency.
 */
export const MarkerIcon = {
  // Transport
  ROAD: 'road',
  LOCATION_MARKER: 'location-marker',
  BUILDING_BRIDGE: 'building-bridge',

  // Safety
  HAZARD_WARNING: 'hazard-warning',
  BARRIER_BLOCK: 'barrier-block',
  TRAFFIC_CONTROL: 'traffic-control',

  // Construction
  CONSTRUCTION: 'construction',
  MAINTENANCE: 'maintenance',

  // Inspection
  INSPECTION: 'inspection',
  VISUAL_CHECK: 'visual-check',
  SURVEY_REPORT: 'survey-report',
  ROAD_SIGN: 'road-sign',
  ISSUE_FOUND: 'issue-found',
} as const;

/**
 * Type representing all valid marker icon keys
 */
export type MarkerIconType = typeof MarkerIcon[keyof typeof MarkerIcon];

/**
 * Icon Category Definitions
 */
export const ICON_CATEGORIES = {
  TRANSPORT: 'transport',
  SAFETY: 'safety',
  CONSTRUCTION: 'construction',
  INSPECTION: 'inspection',
} as const;

/**
 * Type representing all valid icon categories
 */
export type IconCategory = typeof ICON_CATEGORIES[keyof typeof ICON_CATEGORIES];

/**
 * Icon Display Metadata
 *
 * Contains all information needed to display an icon in the UI
 */
export interface IconMetadata {
  /** Unique icon key */
  key: MarkerIconType;
  /** I18n translation key for display name */
  labelKey: string;
  /** Icon category for grouping */
  category: IconCategory;
}

/**
 * Complete Icon Registry with Metadata
 *
 * Single source of truth for all available marker icons.
 * Used by:
 * - MapToolbox icon selector for rendering icon options
 * - Icon mapping utilities for validation
 * - Any component that needs to enumerate available icons
 */
export const ICON_REGISTRY: ReadonlyArray<IconMetadata> = [
  // Transport
  { key: MarkerIcon.ROAD, labelKey: 'tasks.icons.road', category: ICON_CATEGORIES.TRANSPORT },
  { key: MarkerIcon.LOCATION_MARKER, labelKey: 'tasks.icons.locationMarker', category: ICON_CATEGORIES.TRANSPORT },
  { key: MarkerIcon.BUILDING_BRIDGE, labelKey: 'tasks.icons.buildingBridge', category: ICON_CATEGORIES.TRANSPORT },

  // Safety
  { key: MarkerIcon.HAZARD_WARNING, labelKey: 'tasks.icons.hazardWarning', category: ICON_CATEGORIES.SAFETY },
  { key: MarkerIcon.BARRIER_BLOCK, labelKey: 'tasks.icons.barrierBlock', category: ICON_CATEGORIES.SAFETY },
  { key: MarkerIcon.TRAFFIC_CONTROL, labelKey: 'tasks.icons.trafficControl', category: ICON_CATEGORIES.SAFETY },

  // Construction
  { key: MarkerIcon.CONSTRUCTION, labelKey: 'tasks.icons.construction', category: ICON_CATEGORIES.CONSTRUCTION },
  { key: MarkerIcon.MAINTENANCE, labelKey: 'tasks.icons.maintenance', category: ICON_CATEGORIES.CONSTRUCTION },

  // Inspection
  { key: MarkerIcon.INSPECTION, labelKey: 'tasks.icons.inspection', category: ICON_CATEGORIES.INSPECTION },
  { key: MarkerIcon.VISUAL_CHECK, labelKey: 'tasks.icons.visualCheck', category: ICON_CATEGORIES.INSPECTION },
  { key: MarkerIcon.SURVEY_REPORT, labelKey: 'tasks.icons.surveyReport', category: ICON_CATEGORIES.INSPECTION },
  { key: MarkerIcon.ROAD_SIGN, labelKey: 'tasks.icons.roadSign', category: ICON_CATEGORIES.INSPECTION },
  { key: MarkerIcon.ISSUE_FOUND, labelKey: 'tasks.icons.issueFound', category: ICON_CATEGORIES.INSPECTION },
] as const;

/**
 * Helper function to get icons by category
 *
 * @param category - Icon category to filter by
 * @returns Array of icon metadata for the specified category
 */
export const getIconsByCategory = (category: IconCategory): ReadonlyArray<IconMetadata> => {
  return ICON_REGISTRY.filter((icon) => icon.category === category);
};

/**
 * Helper function to validate if a string is a valid icon key
 *
 * @param key - String to validate
 * @returns True if the key is a valid MarkerIconType
 */
export const isValidIconKey = (key: string): key is MarkerIconType => {
  return ICON_REGISTRY.some((icon) => icon.key === key);
};
